import { useMemo, useState } from 'react';
import { PERMISSION_CATEGORIES, permissionLabel } from '@/lib/role-display';

interface RolePermissionMatrixProps {
  selected: string[];
  onChange: (permissions: string[]) => void;
  hideHeader?: boolean;
  readOnly?: boolean;
}

export function RolePermissionMatrix({ selected, onChange, hideHeader = false, readOnly = false }: RolePermissionMatrixProps) {
  const [groupCounts, setGroupCounts] = useState<Record<string, number>>({});

  const totalActive = selected.length;

  const categories = useMemo(
    () =>
      PERMISSION_CATEGORIES.map((cat) => ({
        ...cat,
        activeCount: cat.permissions.filter((p) => selected.includes(p)).length,
      })),
    [selected],
  );

  function syncGroupCounts(nextSelected: string[]) {
    const counts: Record<string, number> = {};
    for (const cat of PERMISSION_CATEGORIES) {
      counts[cat.key] = cat.permissions.filter((p) => nextSelected.includes(p)).length;
    }
    setGroupCounts(counts);
  }

  function setSelected(next: string[]) {
    onChange(next);
    syncGroupCounts(next);
  }

  function togglePermission(permission: string, checked: boolean) {
    if (readOnly) return;
    setSelected(checked ? [...selected, permission] : selected.filter((p) => p !== permission));
  }

  function selectAll() {
    if (readOnly) return;
    const all = PERMISSION_CATEGORIES.flatMap((c) => c.permissions);
    setSelected(all);
  }

  function deselectAll() {
    if (readOnly) return;
    setSelected([]);
  }

  function toggleCategory(categoryKey: string, permissions: string[]) {
    if (readOnly) return;
    const allChecked = permissions.every((p) => selected.includes(p));
    if (allChecked) {
      setSelected(selected.filter((p) => !permissions.includes(p)));
    } else {
      setSelected([...new Set([...selected, ...permissions])]);
    }
  }

  return (
    <section className="lms-permission-matrix">
      {!hideHeader && (
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="pm-headline-md text-[#1a1c1e]">Permisos</h2>
            <p id="role-pm-total-active-label" className="pm-body-md pm-text-variant mt-1">
              {totalActive} {totalActive === 1 ? 'permiso activo' : 'permisos activos'} en total
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            {!readOnly && (
              <>
                <button type="button" className="pm-label-md text-[#142175] hover:underline" onClick={selectAll}>
                  Seleccionar todo
                </button>
                <button type="button" className="pm-label-md pm-text-variant hover:underline" onClick={deselectAll}>
                  Deseleccionar todo
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-12">
        {categories.map((category) => {
          const activeCount = groupCounts[category.key] ?? category.activeCount;
          return (
            <section key={category.key} className="category-section">
              <div className="pm-border mb-4 flex flex-wrap items-center justify-between gap-3 border-b pb-4">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[28px] text-[#142175]" aria-hidden="true">
                    {category.icon}
                  </span>
                  <h3 className="pm-headline-md text-[#1a1c1e]">{category.label}</h3>
                </div>
                <div className="flex items-center gap-4">
                  <span className="pm-active-badge pm-label-sm rounded-full px-3 py-1 font-bold uppercase tracking-wider">
                    {activeCount} {activeCount === 1 ? 'Activo' : 'Activos'}
                  </span>
                  {!readOnly && (
                    <button
                      type="button"
                      className="pm-label-sm flex shrink-0 items-center gap-1 text-[#142175] hover:underline"
                      onClick={() => toggleCategory(category.key, category.permissions)}
                    >
                      <span className="material-symbols-outlined text-sm" aria-hidden="true">
                        done_all
                      </span>
                      Seleccionar todo
                    </button>
                  )}
                </div>
              </div>
              <div className="pm-border pm-radius-xl flex flex-col overflow-hidden border bg-white">
                {category.permissions.map((permission) => {
                  const checked = selected.includes(permission);
                  return (
                    <label
                      key={permission}
                      className={`permission-row group flex items-center gap-4 border-b px-6 py-4 transition-colors pm-border last:border-b-0 ${
                        readOnly ? 'cursor-default opacity-90' : 'cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        name="permissions[]"
                        value={permission}
                        className="pm-checkbox"
                        checked={checked}
                        disabled={readOnly}
                        onChange={(e) => togglePermission(permission, e.target.checked)}
                      />
                      <span className="pm-body-md text-[#1a1c1e] transition-colors group-hover:text-[#142175]">
                        {permissionLabel(permission)}
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
}
